import { motion } from "framer-motion";
import { Edit, CheckCircle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { User } from "@supabase/supabase-js";
import { ProfileData } from "@/types/profile";
import { Link } from "react-router-dom";

interface MobileSettingsHeaderProps {
  profile: ProfileData | null | undefined;
  user: User | null;
}

export function MobileSettingsHeader({ profile, user }: MobileSettingsHeaderProps) {
  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Calculate profile completion
  const calculateCompletion = () => {
    if (!profile) return 0;
    const fields = [
      profile.full_name,
      profile.bio,
      profile.phone,
      profile.location,
      profile.avatar_url,
      profile.date_of_birth,
    ];
    const completed = fields.filter(Boolean).length;
    return Math.round((completed / fields.length) * 100);
  };

  const completionPercentage = calculateCompletion();
  const isVerified = user?.email_confirmed_at;

  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b"
    >
      <Card className="mx-4 my-3 border-0 shadow-sm">
        <div className="p-4 flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-12 w-12 border-2 border-primary/20">
              <AvatarImage src={profile?.avatar_url} alt={profile?.full_name} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {getInitials(profile?.full_name)}
              </AvatarFallback>
            </Avatar>
            
            {/* Profile completion ring */}
            <svg 
              className="absolute -inset-1 -rotate-90" 
              viewBox="0 0 100 100"
            >
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                className="text-muted/20"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeDasharray={`${completionPercentage * 2.83} 283`}
                className={cn(
                  "transition-all duration-500",
                  completionPercentage === 100 ? "text-green-500" : "text-primary"
                )}
              />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold truncate">
                {profile?.full_name || "User"}
              </h2>
              {isVerified && (
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px] gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Verified
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground truncate">
              {user?.email}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {completionPercentage}% complete
            </p>
          </div>

          <Link to="/settings?tab=profile">
            <Button 
              size="icon" 
              variant="outline"
              className="h-9 w-9 shrink-0"
              aria-label="Edit profile"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </Card>
    </motion.div>
  );
}
